import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import apiService from '../../services/api'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import {
  HiOutlineMagnifyingGlass,
  HiOutlinePaperAirplane,
  HiOutlineCheckCircle,
} from 'react-icons/hi2'
import {
  Avatar,
  Banner,
  Button,
  Card,
  FormField,
  Input,
  Spinner,
} from '../../components/ui'

const connectionRequestSchema = z.object({
  receiver_account_id:   z.string().min(1, 'Please select an account'),
  receiver_account_name: z.string().min(1, 'Account name is required'),
})

export default function SendConnectionRequest({ onClose }) {
  const { t } = useTranslation()
  const { getEntity } = useWorkspace()
  const ownerTerm = getEntity('owner')
  const connectionTerm = getEntity('connection')

  const [isLoading, setIsLoading] = useState(false)
  const [apiError, setApiError] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [requestSent, setRequestSent] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState(null)

  const {
    register, handleSubmit, formState: { errors }, setValue, reset,
  } = useForm({ resolver: zodResolver(connectionRequestSchema) })

  const searchAccounts = async (query) => {
    if (!query || query.trim().length < 1) { setSearchResults([]); return }
    try {
      setSearching(true)
      const response = await apiService.get('/accounts/search/', { params: { q: query } })
      setSearchResults(response.success ? (response.data.data || []) : [])
    } catch { setSearchResults([]) } finally { setSearching(false) }
  }

  const handleSearchChange = (e) => {
    const query = e.target.value
    setSearchQuery(query)
    clearTimeout(window.searchTimeout)
    window.searchTimeout = setTimeout(() => searchAccounts(query), 300)
  }

  const selectAccount = (account) => {
    setSelectedAccount(account)
    setValue('receiver_account_id', account.id.toString())
    setValue('receiver_account_name', account.name)
    setSearchResults([])
    setSearchQuery(account.name)
  }

  const onSubmit = async (data) => {
    try {
      setIsLoading(true)
      setApiError('')
      const payload = {
        receiver_account_id: parseInt(data.receiver_account_id),
        receiver_account_name: data.receiver_account_name,
      }
      const response = await apiService.post('/send-connection-request/', payload)
      if (response.success) {
        setRequestSent(true)
        reset()
        setSelectedAccount(null)
        setSearchQuery('')
        if (onClose) setTimeout(() => onClose(), 2000)
      } else {
        setApiError(response.error?.message || t('connections.send.submit', { conn: connectionTerm.singular.toLowerCase() }))
      }
    } catch {
      setApiError(t('common.networkError'))
    } finally {
      setIsLoading(false)
    }
  }

  if (requestSent) {
    return (
      <Card padding="lg">
        <div className="text-center py-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-success-soft text-success mx-auto mb-4">
            <HiOutlineCheckCircle className="w-9 h-9" />
          </div>
          <h3 className="text-lg font-semibold text-ink-primary mb-2">
            {t('connections.send.successTitle', { conn: connectionTerm.singular })}
          </h3>
          <p className="text-sm text-ink-secondary mb-5">
            {t('connections.send.successMessage', {
              conn: connectionTerm.singular.toLowerCase(),
              name: selectedAccount?.name,
            })}
          </p>
          <Button onClick={() => setRequestSent(false)}>
            {t('connections.send.sendAnother', { conn: connectionTerm.singular.toLowerCase() })}
          </Button>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-5">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {apiError && <Banner tone="danger">{apiError}</Banner>}

        <FormField label={t('connections.send.searchLabel', { owner: ownerTerm.singular })} error={errors.receiver_account_id?.message}>
          <Input
            leadingIcon={HiOutlineMagnifyingGlass}
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={t('connections.send.searchPlaceholder', { owner: ownerTerm.singular.toLowerCase() })}
            trailingAction={searching ? <Spinner size="sm" className="me-2" /> : null}
          />
        </FormField>

        {searchResults.length > 0 && (
          <Card variant="bordered" padding="none">
            <ul className="max-h-60 overflow-y-auto divide-y divide-border-subtle">
              {searchResults.map((account) => (
                <li key={account.id}>
                  <button
                    type="button"
                    onClick={() => selectAccount(account)}
                    className="w-full p-3 text-start hover:bg-muted transition-colors flex items-center gap-3"
                  >
                    <Avatar name={account.name} size="sm" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-ink-primary truncate">{account.name}</p>
                      <p className="text-xs text-ink-muted">{t('connections.send.ownerLabel', { name: account.owner_name })}</p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {selectedAccount && (
          <Card padding="md" className="bg-brand-soft/40 border-brand/30">
            <div className="flex items-center gap-3">
              <Avatar name={selectedAccount.name} size="md" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink-primary truncate">{selectedAccount.name}</p>
                <p className="text-xs text-ink-secondary">{t('connections.send.ownerLabel', { name: selectedAccount.owner_name })}</p>
              </div>
            </div>
          </Card>
        )}

        <input type="hidden" {...register('receiver_account_id')} />
        <input type="hidden" {...register('receiver_account_name')} />

        <Button
          type="submit"
          fullWidth
          leadingIcon={HiOutlinePaperAirplane}
          loading={isLoading}
          disabled={!selectedAccount}
        >
          {t('connections.send.submit', { conn: connectionTerm.singular.toLowerCase() })}
        </Button>
      </form>

      <Banner tone="info" title={t('connections.send.guideTitle', { conn: connectionTerm.singular.toLowerCase() })}>
        <ul className="text-sm space-y-1 list-disc list-inside ms-1">
          <li>{t('connections.send.guide1', { owner: ownerTerm.singular.toLowerCase() })}</li>
          <li>{t('connections.send.guide2')}</li>
          <li>{t('connections.send.guide3')}</li>
          <li>{t('connections.send.guide4', {
            cases: getEntity('case').plural.toLowerCase(),
            documents: getEntity('document').plural.toLowerCase(),
          })}</li>
          <li>{t('connections.send.guide5')}</li>
        </ul>
      </Banner>
    </div>
  )
}
